import { getToken, onMessage } from 'firebase/messaging';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove,
  Timestamp,
  collection,
  getDocs
} from 'firebase/firestore';
import { messaging, db } from './firebase';
import { createNotification, NOTIFICATION_TYPES } from './notificationService';
import { pushToast } from '../providers/NotificationToastProvider';

// VAPID key - loaded from environment variables
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

/**
 * Request notification permission and get FCM token
 * @param {string} userId - User ID
 * @returns {Object} Result with token
 */
export const requestNotificationPermission = async (userId) => {
  try {
    if (!messaging) {
      console.warn('Firebase Messaging not supported');
      return { success: false, error: 'Messaging not supported' };
    }

    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return { success: false, error: 'Permission denied' };
    }

    // Get FCM token
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    
    if (token) {
      console.log('FCM Token:', token);
      
      // Save token to Firestore
      await saveFCMToken(userId, token);
      
      return { success: true, token };
    } else {
      console.log('No registration token available');
      return { success: false, error: 'No token available' };
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Ensure the current user has an FCM token saved
 * Only requests permission / generates token if missing
 * @param {string} userId
 */
export const ensureUserHasFCMToken = async (userId) => {
  if (!userId) {
    return { success: false, error: 'User ID is required' };
  }

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const existingToken = userDoc.exists() ? userDoc.data()?.fcmToken : null;

    if (existingToken) {
      return { success: true, token: existingToken, alreadyExists: true };
    }

    if (typeof window === 'undefined' || !('Notification' in window)) {
      return { success: false, error: 'Notifications are not supported in this environment' };
    }

    if (Notification.permission === 'denied') {
      console.warn('Notification permission was previously denied');
      return { success: false, error: 'Notification permission denied' };
    }

    // If permission is default or granted, request/generate the token
    return await requestNotificationPermission(userId);
  } catch (error) {
    console.error('Error ensuring FCM token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Save FCM token to Firestore
 * @param {string} userId - User ID
 * @param {string} token - FCM token
 */
export const saveFCMToken = async (userId, token) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      await updateDoc(userRef, {
        fcmToken: token,
        fcmTokenUpdatedAt: Timestamp.now()
      });
    } else {
      await setDoc(userRef, {
        fcmToken: token,
        fcmTokenUpdatedAt: Timestamp.now()
      }, { merge: true });
    }
    
    console.log('FCM token saved successfully');
    return { success: true };
  } catch (error) {
    console.error('Error saving FCM token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe user to notification topics
 * @param {string} userId - User ID
 * @param {Array} topics - Array of topics to subscribe
 */
export const subscribeToTopics = async (userId, topics) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    // Save subscribed topics to user document
    await updateDoc(userRef, {
      subscribedTopics: arrayUnion(...topics),
      topicsUpdatedAt: Timestamp.now()
    });
    
    console.log('Subscribed to topics:', topics);
    return { success: true, topics };
  } catch (error) {
    console.error('Error subscribing to topics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Unsubscribe user from notification topics
 * @param {string} userId - User ID
 * @param {Array} topics - Array of topics to unsubscribe
 */
export const unsubscribeFromTopics = async (userId, topics) => {
  try {
    const userRef = doc(db, 'users', userId);
    
    await updateDoc(userRef, {
      subscribedTopics: arrayRemove(...topics),
      topicsUpdatedAt: Timestamp.now()
    });
    
    console.log('Unsubscribed from topics:', topics);
    return { success: true, topics };
  } catch (error) {
    console.error('Error unsubscribing from topics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user's subscribed topics
 * @param {string} userId - User ID
 * @returns {Object} Result with topics array
 */
export const getUserSubscribedTopics = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { success: false, error: 'User not found' };
    }
    
    const topics = userDoc.data().subscribedTopics || [];
    return { success: true, topics };
  } catch (error) {
    console.error('Error getting subscribed topics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Setup foreground message listener
 * @param {Function} callback - Callback function to handle messages
 */
export const setupMessageListener = (callback) => {
  if (!messaging) {
    console.warn('Firebase Messaging not supported');
    return null;
  }
  
  return onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);
    
    // Show notification
    if (payload.notification) {
      const { title, body, icon } = payload.notification;
      
      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          icon: icon || '/logo192.png',
          badge: '/logo192.png'
        });
      }

      // Trigger in-app toast
      pushToast({
        title,
        message: body,
        type: payload.data?.type || 'info'
      });
    }
    
    // Call callback with payload
    if (callback) {
      callback(payload);
    }
  });
};

/**
 * Create notification request for backend to send
 * This creates a document in Firestore that triggers a Cloud Function
 * @param {Object} notificationData - Notification data
 */
export const sendNotificationRequest = async (notificationData) => {
  try {
    const {
      type, // 'topic' or 'personal'
      topic, // For topic-based notifications
      userId, // For personal notifications
      title,
      body,
      data
    } = notificationData;
    
    const notificationDoc = {
      type,
      title,
      body,
      data: data || {},
      createdAt: Timestamp.now(),
      status: 'pending'
    };
    
    if (type === 'topic') {
      notificationDoc.topic = topic;
    } else if (type === 'personal') {
      notificationDoc.userId = userId;
    }
    
    // Save to Firestore - Cloud Function will process this
    const docRef = await setDoc(
      doc(db, 'notificationQueue', `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`),
      notificationDoc
    );
    
    console.log('Notification request created');
    return { success: true };
  } catch (error) {
    console.error('Error creating notification request:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send notification to all users subscribed to specific topics
 * @param {Array} topics - Array of topics (e.g., ['industry_Technology', 'education_Bachelor'])
 * @param {Object} notification - Notification data
 */
export const sendTopicNotification = async (topics, notification) => {
  try {
    const { title, body, data } = notification;
    
    // Create notification requests for each topic (for FCM push)
    const promises = topics.map(topic => 
      sendNotificationRequest({
        type: 'topic',
        topic,
        title,
        body,
        data
      })
    );
    
    await Promise.all(promises);
    console.log('Topic notifications queued for:', topics);
    return { success: true, topics };
  } catch (error) {
    console.error('Error sending topic notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send personal notification to a specific user
 * @param {string} userId - User ID
 * @param {Object} notification - Notification data
 */
export const sendPersonalNotification = async (userId, notification) => {
  const { title, body, data = {} } = notification;
  let pushResult = { success: false };
  let inAppResult = { success: false };

  // Queue FCM push notification. If this fails, still create the in-app
  // notification so the notification bell has a message to show.
  try {
    pushResult = await sendNotificationRequest({
      type: 'personal',
      userId,
      title,
      body,
      data
    });
  } catch (pushError) {
    console.error('Error queueing push notification:', pushError);
  }

  // Also create in-app notification
  try {
    let notificationType = NOTIFICATION_TYPES.NEW_APPLICATION;
    if (data.type === 'new_application') {
      notificationType = NOTIFICATION_TYPES.NEW_APPLICATION;
    } else if (data.type === 'application_submitted') {
      notificationType = NOTIFICATION_TYPES.APPLICATION_SUBMITTED;
    } else if (data.type === 'application_status_update') {
      notificationType = NOTIFICATION_TYPES.APPLICATION_STATUS_UPDATE;
    } else if (data.type === 'new_job') {
      notificationType = NOTIFICATION_TYPES.NEW_JOB;
    }

    inAppResult = await createNotification({
      userId,
      type: notificationType,
      title,
      message: body,
      data
    });
  } catch (inAppError) {
    console.error('Error creating in-app notification:', inAppError);
  }

  if (pushResult.success || inAppResult.success) {
    console.log('Personal notification sent to user:', userId);
    return { success: true, userId, push: pushResult, inApp: inAppResult };
  }

  return {
    success: false,
    error: pushResult.error || inAppResult.error || 'Failed to send notification'
  };
};

/**
 * Generate topic names from job data
 * @param {Object} jobData - Job data
 * @returns {Array} Array of topic names
 */
export const generateTopicsFromJob = (jobData) => {
  const topics = [];
  
  // Add industry topic
  if (jobData.industry) {
    topics.push(`industry_${jobData.industry.replace(/\s+/g, '_')}`);
  }
  
  // Add education topic
  if (jobData.education) {
    topics.push(`education_${jobData.education.replace(/\s+/g, '_')}`);
  }
  
  return topics;
};

/**
 * Get available notification topics for job seekers
 * @returns {Object} Object with industry and education topics
 */
export const getAvailableTopics = () => {
  return {
    industries: [
      'Technology',
      'Information Technology',
      'Software Development',
      'Healthcare',
      'Finance',
      'Banking',
      'Education',
      'Manufacturing',
      'Retail',
      'E-commerce',
      'Telecommunications',
      'Automotive',
      'Aerospace',
      'Construction',
      'Real Estate',
      'Hospitality',
      'Tourism',
      'Food & Beverage',
      'Agriculture',
      'Energy',
      'Utilities',
      'Transportation',
      'Logistics',
      'Consulting',
      'Legal',
      'Marketing',
      'Advertising',
      'Media & Entertainment',
      'Sports',
      'Non-Profit',
      'Government',
      'Other'
    ],
    educationRequirements: [
      'Bachelor\'s in Computer Science',
      'Bachelor\'s in Information Technology',
      'Bachelor\'s in Software Engineering',
      'Bachelor\'s in Data Science',
      'Bachelor\'s in Engineering',
      'Bachelor\'s in Business Administration',
      'Bachelor\'s in Commerce (B.Com)',
      'Bachelor\'s in Marketing',
      'Bachelor\'s in Finance',
      'Master\'s in Computer Science',
      'Master\'s in Business Administration (MBA)',
      'Master\'s in Data Science',
      'Any Engineering Degree',
      'Any Graduate Degree',
      'Any Post Graduate Degree',
      'High School Diploma',
      '12th Pass',
      'Graduate (Any Stream)',
      'Post Graduate (Any Stream)'
    ]
  };
};

export const sendJobMatchingNotifications = async (jobData, jobId) => {
  try {
    const jobTitle = jobData.title || "";
    const jobType = jobData.jobType || "";
    const workMode = jobData.workMode || "";
    const jobLocation = jobData.location || {};
    const companyName = jobData.company || "A company";

    console.log(`🔍 [FCM] Finding matched job seekers for Job: ${jobTitle} (${jobId})`);

    // Fetch all seeker profiles
    const profilesSnap = await getDocs(collection(db, 'seekerProfiles'));
    const matchedUsers = [];

    profilesSnap.docs.forEach((profileDoc) => {
      const profile = profileDoc.data();
      const userId = profile.userId || profileDoc.id;
      const preferences = profile.preferences || {};

      const targetRoles = preferences.targetRoles || [];
      const jobTypes = preferences.jobTypes || [];
      const workModes = preferences.workModes || [];
      const preferredLocations = preferences.preferredLocations || [];

      // Match Role (substring inclusion check)
      const matchesRole = targetRoles.length === 0 || targetRoles.some(role => 
        jobTitle.toLowerCase().includes(role.toLowerCase()) || 
        role.toLowerCase().includes(jobTitle.toLowerCase())
      );

      // Match Job Type
      const matchesJobType = jobTypes.length === 0 || jobTypes.some(type => 
        type.toLowerCase() === jobType.toLowerCase()
      );

      // Match Work Mode
      const matchesWorkMode = workModes.length === 0 || workModes.some(mode => 
        mode.toLowerCase() === workMode.toLowerCase()
      );

      // Match Location
      const matchesLocation = preferredLocations.length === 0 || 
        preferredLocations.some(loc => 
          loc.toLowerCase() === (jobLocation.city || '').toLowerCase() ||
          loc.toLowerCase() === (jobLocation.state || '').toLowerCase()
        ) || (workMode.toLowerCase() === 'work from home' || workMode.toLowerCase() === 'work from home (remote)');

      if (matchesRole && matchesJobType && matchesWorkMode && matchesLocation) {
        matchedUsers.push(userId);
      }
    });

    console.log(`🎯 [FCM] Found ${matchedUsers.length} matched users for Job: ${jobTitle}`);

    // Send notifications to all matching users
    const notificationPromises = matchedUsers.map((userId) => {
      return sendPersonalNotification(userId, {
        title: `New Job Match: ${jobTitle}`,
        body: `${companyName} is hiring for ${jobTitle} matching your job preferences. Apply now!`,
        data: {
          jobId,
          jobTitle,
          company: companyName,
          type: 'new_job'
        }
      });
    });

    await Promise.all(notificationPromises);
    return { success: true, matchedCount: matchedUsers.length };
  } catch (error) {
    console.error('Error sending job matching notifications:', error);
    return { success: false, error: error.message };
  }
};

export default {
  requestNotificationPermission,
  saveFCMToken,
  subscribeToTopics,
  unsubscribeFromTopics,
  getUserSubscribedTopics,
  setupMessageListener,
  sendTopicNotification,
  sendPersonalNotification,
  generateTopicsFromJob,
  getAvailableTopics,
  sendJobMatchingNotifications
};
